import { useLocalObservable } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';

import { useTranslation } from '@gem/modules/i18n';

import Tasks from 'APP/Tasks';
import { IStarsFeedbackPresenter } from 'APP/main/Popups/StarsFeedback/StarsFeedback.types';
import { storage } from 'APP/packages/storage';
import Entities from 'STORE';

const MAX_NEGATIVE_RATE = 4;

const MAX_LIVE_TIME = 60000;

export const useStarsFeedbackPresenter = (): IStarsFeedbackPresenter => {
  const { t } = useTranslation();
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearAutoCloseTimer = (): void => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
  };

  const presenter = useLocalObservable<IStarsFeedbackPresenter>(() => ({
    starsArray: [5, 4, 3, 2, 1],
    isDontShowChecked: false,

    get isShown(): boolean {
      return Entities.feedback.isStarsShown;
    },

    get channelId(): string | null {
      return Entities.feedback.channelId;
    },

    onDontShowChange: (value: boolean): void => {
      if (value) {
        Entities.toast.show(t('feedback_form_not_received'));
      }
      presenter.isDontShowChecked = value;
    },

    onClose: (): void => {
      if (presenter.isDontShowChecked) {
        storage.feedback.setDontShowValue(true);
      }
      Entities.feedback.reset();
    },

    onStarClick: (star: number): void => {
      if (!presenter.channelId) {
        return;
      }

      if (presenter.isDontShowChecked) {
        storage.feedback.setDontShowValue(true);
      }

      clearAutoCloseTimer();

      if (star > MAX_NEGATIVE_RATE) {
        Tasks.feedback.sendFeedback({ rating: String(star), channelId: presenter.channelId });
        Entities.feedback.reset();
      } else {
        Entities.feedback.setStarsRate(star);
        Entities.feedback.setIsDescriptionShown(true);
        Entities.feedback.setIsStarsShown(false);
      }
    },
  }));

  useEffect(() => {
    clearAutoCloseTimer();

    if (presenter.isShown) {
      timeoutIdRef.current = setTimeout(() => {
        presenter.onClose();
      }, MAX_LIVE_TIME);
    }

    return () => {
      clearAutoCloseTimer();
    };
  }, [presenter.isShown]);

  return presenter;
};
